import React from 'react';
import { API_BASE_URL } from './config';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import CollectionsIcon from '@mui/icons-material/Collections';
import CircularProgress from '@mui/material/CircularProgress';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const PRIMARY = '#1d5576';

/**
 * Renders a string that may contain markdown links [label](url).
 * Links that look like file downloads get a styled download chip;
 * other links get a regular anchor tag.
 */
const LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;

// Fetch a proxy download URL with the current auth token and trigger a browser download.
// Using fetch() instead of <a href> so the token is read fresh on click (never stale).
const handleProxyDownload = async (url, filename = 'download', setLoading = null) => {
  const token = localStorage.getItem('access') || '';
  try {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error('Download failed:', err);
  } finally {
    if (setLoading) setLoading(false);
  }
};

// Walk chat messages backwards and return the DB product ids from the most recent
// assistant message that actually showed products — either as product cards, or as a
// catalog/pricing table backed by real DB products (table_data.product_ids). Returns
// null if nothing exportable has been shown yet, so callers can fall back to the normal
// chat flow instead of faking a result.
export function findExportableProductIds(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'assistant') continue;
    if (m.product_cards && m.product_cards.length > 0) {
      const ids = m.product_cards.map((c) => c.id).filter(Boolean);
      if (ids.length > 0) return ids;
    }
    if (m.table_data && m.table_data.product_ids && m.table_data.product_ids.length > 0) {
      return m.table_data.product_ids;
    }
  }
  return null;
}

// POST product_ids to a backend export endpoint and save the returned file (Excel/ZIP).
export const handleProductIdsExport = async (url, productIds, filename, setLoading) => {
  const token = localStorage.getItem('access') || '';
  try {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ product_ids: productIds }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error('Export failed:', err);
  } finally {
    if (setLoading) setLoading(false);
  }
};

// Chip with built-in loading state for catalog/file downloads
const DownloadChip = ({ url, filename = 'download', label = 'Download', compact }) => {
  const [loading, setLoading] = React.useState(false);

  const handleClick = () => {
    if (loading) return;
    if (!url.startsWith('/api/')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setLoading(true);
    handleProxyDownload(url, filename, setLoading);
  };

  return (
    <Chip
      onClick={handleClick}
      label={loading ? 'Downloading...' : label}
      size="small"
      icon={
        loading
          ? <CircularProgress size={11} sx={{ color: 'white !important', ml: '4px !important' }} />
          : <FileDownloadIcon sx={{ fontSize: '14px !important' }} />
      }
      clickable={!loading}
      sx={{
        bgcolor: loading ? '#888' : PRIMARY,
        color: 'white',
        fontSize: compact ? '0.68rem' : '0.72rem',
        height: 24,
        cursor: loading ? 'default' : 'pointer',
        '& .MuiChip-icon': { color: 'white' },
        '&:hover': { bgcolor: loading ? '#888' : '#164060' },
      }}
    />
  );
};

function InlineContent({ text }) {
  if (!text) return <>{text}</>;

  // Parse bold (**text**) and markdown links [label](url) inline
  const INLINE_REGEX = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let match;

  while ((match = INLINE_REGEX.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));

    if (match[1] !== undefined) {
      // **bold**
      parts.push(<strong key={match.index}>{match[1]}</strong>);
    } else {
      const label = match[2];
      const url = match[3];
      const isDownload = /\.(pdf|xlsx?|csv|zip|docx?)$/i.test(url) || label.toLowerCase().includes('download');
      parts.push(
        isDownload ? (
          <Box key={match.index} component="span" sx={{ mx: 0.5 }}>
            <DownloadChip url={url} filename={label} label={label} />
          </Box>
        ) : (
          <a key={match.index} href={url} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY, fontWeight: 600 }}>
            {label}
          </a>
        )
      );
    }
    last = match.index + match[0].length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// Renders a block of text with markdown: headings, bullets, bold, links
function MarkdownText({ text, compact }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let bulletBuffer = [];
  let key = 0;

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    elements.push(
      <Box key={key++} component="ul" sx={{ m: 0, pl: 2.5, mb: 0.5 }}>
        {bulletBuffer.map((item, i) => (
          <Box key={i} component="li" sx={{ fontSize: compact ? '0.82rem' : '0.9rem', lineHeight: 1.7, color: '#222' }}>
            <InlineContent text={item} />
          </Box>
        ))}
      </Box>
    );
    bulletBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    // H3 ###
    if (line.startsWith('### ')) {
      flushBullets();
      elements.push(
        <Typography key={key++} variant="body2" sx={{ fontWeight: 700, fontSize: compact ? '0.82rem' : '0.9rem', mt: 0.5, mb: 0.25 }}>
          <InlineContent text={line.slice(4)} />
        </Typography>
      );
      continue;
    }

    // H2 ##
    if (line.startsWith('## ')) {
      flushBullets();
      elements.push(
        <Typography key={key++} variant="body2" sx={{ fontWeight: 700, fontSize: compact ? '0.82rem' : '0.9rem', mt: 0.5, mb: 0.25 }}>
          <InlineContent text={line.slice(3)} />
        </Typography>
      );
      continue;
    }

    // H1 #
    if (line.startsWith('# ')) {
      flushBullets();
      elements.push(
        <Typography key={key++} variant="body2" sx={{ fontWeight: 700, fontSize: compact ? '0.82rem' : '0.9rem', mt: 0.5, mb: 0.25 }}>
          <InlineContent text={line.slice(2)} />
        </Typography>
      );
      continue;
    }

    // Bullet - or *
    const bulletMatch = line.match(/^[-*]\s+(.*)/);
    if (bulletMatch) {
      bulletBuffer.push(bulletMatch[1]);
      continue;
    }

    // Numbered list
    const numberedMatch = line.match(/^\d+\.\s+(.*)/);
    if (numberedMatch) {
      bulletBuffer.push(numberedMatch[1]);
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      flushBullets();
      continue;
    }

    // Normal paragraph
    flushBullets();
    elements.push(
      <Typography key={key++} variant="body2" component="p" sx={{ fontSize: compact ? '0.82rem' : '0.9rem', lineHeight: 1.7, m: 0, color: '#222' }}>
        <InlineContent text={line} />
      </Typography>
    );
  }

  flushBullets();
  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>{elements}</Box>;
}

/**
 * Splits a message string into segments of plain text and markdown tables.
 * Returns an array like: [{ type: 'text', content: '...' }, { type: 'table', headers: [], rows: [] }, ...]
 */
export function parseMessage(content) {
  if (!content) return [{ type: 'text', content: '' }];
  const segments = [];
  const tableRegex = /((?:\|.+\|\s*\n?)+)/g;

  let lastIndex = 0;
  let match;

  while ((match = tableRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const text = content.slice(lastIndex, match.index).trim();
      if (text) segments.push({ type: 'text', content: text });
    }

    const tableBlock = match[1];
    const lines = tableBlock
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.startsWith('|') && l.endsWith('|'));

    if (lines.length >= 3) {
      const isSeparator = (line) => /^\|[\s|:-]+\|$/.test(line);
      const parseCells = (line) =>
        line
          .slice(1, -1)
          .split('|')
          .map((c) => c.trim().replace(/\*\*/g, ''));

      const headers = parseCells(lines[0]);
      const sepIdx = lines.findIndex(isSeparator);
      if (sepIdx !== -1) {
        const rows = lines.slice(sepIdx + 1).map(parseCells);
        segments.push({ type: 'table', headers, rows });
      } else {
        segments.push({ type: 'text', content: tableBlock });
      }
    } else {
      segments.push({ type: 'text', content: tableBlock });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    const text = content.slice(lastIndex).trim();
    if (text) segments.push({ type: 'text', content: text });
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', content });
  }

  return segments;
}

/**
 * Export a table (headers + rows) to an Excel file.
 */
export function exportTableToExcel(headers, rows, filename = 'export') {
  const wsData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Style header row (bold, background) — basic column widths
  const colWidths = headers.map((h) => ({ wch: Math.max(h.length + 4, 14) }));
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Results');

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${filename}.xlsx`);
}

// ─── Product Card sub-component ─────────────────────────────────────────────

const DETAIL_ROWS = [
  { key: 'item_number',    label: 'Item #' },
  { key: 'brand',          label: 'Brand' },
  { key: 'upc_code',       label: 'UPC' },
  { key: 'material',       label: 'Material' },
  { key: 'category',       label: 'Category' },
  { key: 'color',          label: 'Color' },
  { key: 'description',    label: 'Description' },
  { key: 'price_list_code',label: 'Price List Code' },
];

// card.image_url comes straight from the chat backend (API/ai_utils.py::get_default_images),
// which already prefers the locally cached, Oracle-verified image over the legacy DB URL -
// no live Oracle fetch needed here.
const OracleCardImage = ({ card, compact, imgError, setImgError }) => {
  const height = compact ? 130 : 180;

  const wrapperSx = {
    width: '100%',
    height,
    bgcolor: '#f7f8fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderBottom: `1px solid ${PRIMARY}15`,
  };

  if (card.image_url && !imgError) {
    return (
      <Box sx={wrapperSx}>
        <img
          src={card.image_url}
          alt={card.product_name}
          onError={() => setImgError(true)}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 8 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={wrapperSx}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#bbb', gap: 0.5 }}>
        <ImageNotSupportedIcon sx={{ fontSize: 36 }} />
        <Typography variant="caption" color="text.disabled">No image</Typography>
      </Box>
    </Box>
  );
};

const ProductCard = ({ card, compact }) => {
  const [imgError, setImgError] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  const details = DETAIL_ROWS.filter(({ key }) => card[key]);

  const handleViewDetails = () => {
    window.open(`/product/${card.id}`, '_blank', 'noopener,noreferrer');
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('access') || '';
      const res = await fetch(`${API_BASE_URL}/api/export-products/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_ids: [card.id] }),
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `product_${card.item_number || card.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${PRIMARY}30`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}
    >
      {/* Image */}
      <OracleCardImage card={card} compact={compact} imgError={imgError} setImgError={setImgError} />

      {/* Product name */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: PRIMARY, fontSize: compact ? '0.8rem' : '0.9rem', lineHeight: 1.3 }}
        >
          {card.product_name}
        </Typography>
      </Box>

      <Divider sx={{ mx: 1.5, borderColor: `${PRIMARY}15` }} />

      {/* Details table — flexGrow pushes buttons to the card bottom */}
      <Box sx={{ px: 1.5, pt: 0.75, pb: 1, flexGrow: 1 }}>
        <Table size="small" sx={{ '& td': { borderBottom: 'none', py: 0.3, px: 0 } }}>
          <TableBody>
            {details.map(({ key, label }) => (
              <TableRow key={key}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: compact ? '0.7rem' : '0.75rem',
                    color: '#555',
                    width: '38%',
                    verticalAlign: 'top',
                    pr: 1,
                  }}
                >
                  {label}:
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: compact ? '0.7rem' : '0.75rem',
                    color: '#222',
                    wordBreak: 'break-word',
                  }}
                >
                  {card[key]}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Buttons */}
      {card.id && (
        <Box sx={{ px: 1.5, pb: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Button
            fullWidth
            size="small"
            variant="contained"
            endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleViewDetails}
            sx={{
              bgcolor: PRIMARY,
              color: 'white',
              fontSize: compact ? '0.72rem' : '0.78rem',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              py: 0.6,
              '&:hover': { bgcolor: '#164060' },
            }}
          >
            View More Details
          </Button>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={exporting
              ? <CircularProgress size={12} sx={{ color: PRIMARY }} />
              : <FileDownloadIcon sx={{ fontSize: '14px !important' }} />}
            onClick={handleExport}
            disabled={exporting}
            sx={{
              borderColor: PRIMARY,
              color: PRIMARY,
              fontSize: compact ? '0.72rem' : '0.78rem',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              py: 0.6,
              '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
              '&.Mui-disabled': { borderColor: '#ccc', color: '#999' },
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─── Table sub-component ────────────────────────────────────────────────────

const TableSegment = ({ headers, rows, compact, tableIndex }) => {
  const filename = `ai_export_table${tableIndex + 1}_${new Date().toISOString().slice(0, 10)}`;

  return (
    <Box>
      {/* Export button row */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <Tooltip title="Export to Excel">
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon sx={{ fontSize: compact ? 14 : 16 }} />}
            onClick={() => exportTableToExcel(headers, rows, filename)}
            sx={{
              fontSize: compact ? '0.68rem' : '0.75rem',
              py: compact ? 0.25 : 0.4,
              px: compact ? 0.75 : 1,
              borderColor: PRIMARY,
              color: PRIMARY,
              textTransform: 'none',
              '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
            }}
          >
            Export to Excel
          </Button>
        </Tooltip>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: `1px solid ${PRIMARY}22`,
          borderRadius: '8px',
          overflowX: compact ? 'hidden' : 'auto',
          width: '100%',
          display: 'block',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((h, hIdx) => (
                <TableCell
                  key={hIdx}
                  sx={{
                    bgcolor: PRIMARY,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: compact ? '0.72rem' : '0.8rem',
                    whiteSpace: compact ? 'normal' : 'nowrap',
                    py: compact ? 0.75 : 1,
                    px: compact ? 1 : 1.5,
                    borderBottom: 'none',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rIdx) => (
              <TableRow
                key={rIdx}
                sx={{
                  '&:nth-of-type(odd)': { bgcolor: `${PRIMARY}06` },
                  '&:hover': { bgcolor: `${PRIMARY}12` },
                }}
              >
                {row.map((cell, cIdx) => (
                  <TableCell
                    key={cIdx}
                    sx={{
                      fontSize: compact ? '0.72rem' : '0.8rem',
                      py: compact ? 0.5 : 0.75,
                      px: compact ? 1 : 1.5,
                      borderColor: `${PRIMARY}15`,
                      whiteSpace: compact ? 'normal' : undefined,
                      wordBreak: compact ? 'break-word' : undefined,
                    }}
                  >
                    <InlineContent text={cell} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Main renderer ──────────────────────────────────────────────────────────

const MessageRenderer = ({ content, compact = false, tableData = null, productCards = null }) => {
  const segments = parseMessage(content);
  let tableCount = 0;
  const [exportingAll, setExportingAll] = React.useState(false);
  const [downloadingImages, setDownloadingImages] = React.useState(false);
  const [exportingTableData, setExportingTableData] = React.useState(false);

  const filename = `catalog_export_${new Date().toISOString().slice(0, 10)}`;
  const cardIds = (productCards || []).map((c) => c.id).filter(Boolean);
  const cardsWithImages = (productCards || []).filter((c) => c.image_url).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.5 : 1 }}>
      {segments.map((seg, idx) => {
        if (seg.type === 'text') {
          return <MarkdownText key={idx} text={seg.content} compact={compact} />;
        }

        // Skip inline tables when product cards are rendered — cards replace the table
        if (productCards && productCards.length > 0) return null;

        const currentTableIndex = tableCount++;
        return (
          <TableSegment
            key={idx}
            headers={seg.headers}
            rows={seg.rows}
            compact={compact}
            tableIndex={currentTableIndex}
          />
        );
      })}

      {/* Product cards rendered directly from backend data */}
      {productCards && productCards.length > 0 && (
        <Box>
          {cardIds.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {cardsWithImages > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={downloadingImages
                    ? <CircularProgress size={12} sx={{ color: PRIMARY }} />
                    : <CollectionsIcon sx={{ fontSize: compact ? 14 : 16 }} />}
                  disabled={downloadingImages}
                  onClick={() => {
                    setDownloadingImages(true);
                    handleProductIdsExport(
                      '/api/download-product-images/',
                      cardIds,
                      `product_images_${cardIds.length}.zip`,
                      setDownloadingImages,
                    );
                  }}
                  sx={{
                    fontSize: compact ? '0.68rem' : '0.75rem',
                    py: compact ? 0.25 : 0.4,
                    px: compact ? 0.75 : 1,
                    borderColor: PRIMARY,
                    color: PRIMARY,
                    textTransform: 'none',
                    '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
                  }}
                >
                  {downloadingImages ? 'Downloading...' : 'Download Images (ZIP)'}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={exportingAll
                  ? <CircularProgress size={12} sx={{ color: PRIMARY }} />
                  : <FileDownloadIcon sx={{ fontSize: compact ? 14 : 16 }} />}
                disabled={exportingAll}
                onClick={() => {
                  setExportingAll(true);
                  handleProductIdsExport(
                    '/api/export-products/',
                    cardIds,
                    `products_${cardIds.length}.xlsx`,
                    setExportingAll,
                  );
                }}
                sx={{
                  fontSize: compact ? '0.68rem' : '0.75rem',
                  py: compact ? 0.25 : 0.4,
                  px: compact ? 0.75 : 1,
                  borderColor: PRIMARY,
                  color: PRIMARY,
                  textTransform: 'none',
                  '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
                }}
              >
                {exportingAll ? 'Exporting...' : 'Export All to Excel'}
              </Button>
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: productCards.length === 1
                ? '1fr'
                : compact
                  ? 'repeat(2, 1fr)'
                  : 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 1.5,
              mt: 0.5,
            }}
          >
            {productCards.map((card, i) => (
              <ProductCard key={card.id ?? i} card={card} compact={compact} />
            ))}
          </Box>
        </Box>
      )}

      {/* Structured catalog table rendered directly from backend data */}
      {tableData && (
        <Box>
          {tableData.image_url && (
            <Box
              sx={{
                width: compact ? 140 : 180,
                height: compact ? 140 : 180,
                mb: 1,
                bgcolor: '#f7f8fa',
                border: `1px solid ${PRIMARY}22`,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={tableData.image_url.startsWith('/media/') ? `${API_BASE_URL}${tableData.image_url}` : tableData.image_url}
                alt={tableData.rows?.[0]?.[1] || 'Product'}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 8 }}
              />
            </Box>
          )}
          {/* Source reference */}
          {tableData.source && (
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5, fontSize: compact ? '0.7rem' : '0.75rem' }}>
              Source: <strong>{tableData.source}</strong>{tableData.updated_at ? ` — Updated: ${tableData.updated_at}` : ''}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <Tooltip title="Export to Excel">
              <Button
                size="small"
                variant="outlined"
                startIcon={exportingTableData
                  ? <CircularProgress size={12} sx={{ color: PRIMARY }} />
                  : <FileDownloadIcon sx={{ fontSize: compact ? 14 : 16 }} />}
                disabled={exportingTableData}
                onClick={() => {
                  // Items backed by a real DB product export through the same
                  // image-embedding endpoint the Product Catalog page uses.
                  if (tableData.product_ids && tableData.product_ids.length > 0) {
                    setExportingTableData(true);
                    handleProductIdsExport(
                      '/api/export-products/',
                      tableData.product_ids,
                      `${filename}.xlsx`,
                      setExportingTableData,
                    );
                  } else {
                    exportTableToExcel(tableData.headers, tableData.rows, filename);
                  }
                }}
                sx={{
                  fontSize: compact ? '0.68rem' : '0.75rem',
                  py: compact ? 0.25 : 0.4,
                  px: compact ? 0.75 : 1,
                  borderColor: PRIMARY,
                  color: PRIMARY,
                  textTransform: 'none',
                  '&:hover': { bgcolor: `${PRIMARY}10`, borderColor: PRIMARY },
                }}
              >
                {exportingTableData ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Tooltip>
          </Box>
          <TableContainer
            sx={{
              border: `1px solid ${PRIMARY}22`,
              borderRadius: '8px',
              overflowX: compact ? 'hidden' : 'auto',
              width: '100%',
              display: 'block',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableData.headers.map((h, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        bgcolor: PRIMARY,
                        color: 'white',
                        fontWeight: 700,
                        fontSize: compact ? '0.72rem' : '0.8rem',
                        whiteSpace: compact ? 'normal' : 'nowrap',
                        py: compact ? 0.75 : 1,
                        px: compact ? 1 : 1.5,
                        borderBottom: 'none',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.rows.map((row, rIdx) => (
                  <TableRow
                    key={rIdx}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: `${PRIMARY}06` },
                      '&:hover': { bgcolor: `${PRIMARY}12` },
                    }}
                  >
                    {row.map((cell, cIdx) => (
                      <TableCell
                        key={cIdx}
                        sx={{
                          fontSize: compact ? '0.72rem' : '0.8rem',
                          py: compact ? 0.5 : 0.75,
                          px: compact ? 1 : 1.5,
                          borderColor: `${PRIMARY}15`,
                          whiteSpace: compact ? 'normal' : undefined,
                          wordBreak: compact ? 'break-word' : undefined,
                        }}
                      >
                        {/* Only render as download chip if the last header is "Download" */}
                        {cIdx === row.length - 1 && cell &&
                         tableData.headers[tableData.headers.length - 1] === 'Download' ? (
                          <DownloadChip
                            url={cell}
                            filename={row[0] || 'catalog'}
                            label="Download"
                            compact={compact}
                          />
                        ) : (
                          <InlineContent text={cell} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Download button for the source catalog */}
          {tableData.download_url && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.7rem' : '0.75rem' }}>
                To view the full catalog:
              </Typography>
              <DownloadChip
                url={tableData.download_url}
                filename={tableData.source || 'catalog'}
                label={`Download ${tableData.source || 'Catalog'}`}
                compact={compact}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MessageRenderer;
